import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  IconButton,
  Chip,
  Divider,
  Paper,
  Fade,
  Slide
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import InfoIcon from '@mui/icons-material/Info';
import WarningIcon from '@mui/icons-material/Warning';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import DeliveryDiningIcon from '@mui/icons-material/DeliveryDining';
import ShoppingBagIcon from '@mui/icons-material/ShoppingBag';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const LimitInfoModal = ({ open, onClose, language, product }) => {
  const hasProductLimit = product?.product_limit > 0;
  const hasCategoryLimit = product?.category?.card_limit > 0;
  const isTableMode = true; // This should come from your context

  const message = {
    en: {
      title: "Order Limit Information",
      subtitle: "Fair Service for All Customers",
      description: "To serve both dining levels and online orders efficiently, we've limited orders for this item.",
      product_limit: "Product Limit",
      category_limit: "Category Limit",
      current_limit: "Current Limit",
      reason: "Why this limit?",
      reason_text: "This helps us ensure:\nFair distribution among all customers\nEfficient kitchen operations\nQuality service for both dine-in and takeaway orders\nBetter dining experience for everyone",
      thank_you: "Thank you for your understanding!",
      got_it: "Got it",
      product_msg: "We've limited orders for this product to ensure fair service for all customers.",
      category_msg: "We've limited orders for this category to ensure fair service for all customers.",
      product_limited: "Product is limited",
      category_limited: "Category is limited"
    },
    ar: {
      title: "معلومات حد الطلب",
      subtitle: "خدمة عادلة لجميع الزبائن",
      description: "لضمان تقديم خدمة فعّالة لكلٍ من زبائن الصالة والطلبات عبر الإنترنت، قمنا بتحديد عدد طلبات هذا المنتج.",
      product_limit: "حد المنتج",
      category_limit: "حد القسم",
      current_limit: "الحد الحالي",
      reason: "لماذا هذا الحد؟",
      reason_text: "هذا يساعدنا في ضمان:\nتوزيع عادل بين جميع الزبائن\nتشغيل المطبخ بكفاءة\nخدمة عالية الجودة لطلبات الصالة والإنترنت\nتجربة طعام أفضل للجميع",
      thank_you: "شكراً لتفهمكم!",
      got_it: "فهمت",
      product_msg: "قمنا بتحديد عدد طلبات هذا المنتج لضمان خدمة عادلة لجميع الزبائن.",
      category_msg: "قمنا بتحديد عدد طلبات هذا القسم لضمان خدمة عادلة لجميع الزبائن.",
      product_limited: "المنتج محدود",
      category_limited: "القسم محدود"
    }
  };

  const currentLang = language === 'ar' ? 'ar' : 'en';
  const msg = message[currentLang];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      TransitionComponent={Transition}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '24px',
          background: 'linear-gradient(135deg, #0D0D0D 0%, #1A1A1A 100%)',
          border: '1px solid rgba(255, 215, 0, 0.2)',
          boxShadow: '0 20px 40px rgba(0, 0, 0, 0.5)'
        }
      }}
    >
      <DialogTitle sx={{ 
        pb: 1,
        borderBottom: '1px solid rgba(255, 215, 0, 0.1)',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: '12px',
              background: 'linear-gradient(135deg, #FFD700 0%, #FF8C00 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <WarningIcon sx={{ color: '#0D0D0D' }} />
          </Box>
          <Box>
            <Typography variant="h6" sx={{ color: '#FFD700', fontWeight: 700 }}>
              {msg.title}
            </Typography>
            <Typography variant="caption" sx={{ color: '#B3B3B3' }}>
              {msg.subtitle}
            </Typography>
          </Box>
        </Box>
        <IconButton onClick={onClose} sx={{ color: '#B3B3B3', '&:hover': { color: '#FFD700' } }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ py: 3 }}>
        <Fade in={open} timeout={300}>
          <Box>
            {/* Main Message */}
            <Paper
              elevation={0}
              sx={{
                p: 2.5,
                mb: 3,
                borderRadius: '16px',
                background: 'rgba(255, 215, 0, 0.05)',
                border: '1px solid rgba(255, 215, 0, 0.1)'
              }}
            >
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                <RestaurantIcon sx={{ color: '#FFD700', fontSize: 32 }} />
                <Typography variant="body1" sx={{ color: '#FFFFFF', lineHeight: 1.6 }}>
                  {language === 'ar' 
                    ? 'لضمان تقديم خدمة فعّالة لكلٍ من زبائن الصالة والطلبات عبر الإنترنت، قمنا بتحديد عدد طلبات وجبات القسم او المنتج. نشكركم على تفهّمكم.'
                    : "To serve both dining levels and takeaway orders efficiently, we've limited orders for this product or category meals. Thanks for your understanding."}
                </Typography>
              </Box>
            </Paper>

            {/* Limit Information */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" sx={{ color: '#FFD700', mb: 1.5, fontWeight: 600 }}>
                {msg.current_limit}
              </Typography>
              
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {hasProductLimit && (
                  <Paper
                    sx={{
                      p: 1.5,
                      borderRadius: '12px',
                      background: 'rgba(255, 215, 0, 0.08)',
                      border: '1px solid rgba(255, 215, 0, 0.15)'
                    }}
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <ShoppingBagIcon sx={{ color: '#FFD700', fontSize: 20 }} />
                        <Typography variant="body2" sx={{ color: '#FFFFFF' }}>
                          {msg.product_limit}
                        </Typography>
                      </Box>
                      <Chip
                        label={`${product.product_limit} ${language === 'ar' ? 'منتج' : 'items'}`}
                        size="small"
                        sx={{
                          backgroundColor: '#FFD700',
                          color: '#000000',
                          fontWeight: 700,
                          fontSize: '0.75rem'
                        }}
                      />
                    </Box>
                    <Typography variant="caption" sx={{ color: '#B3B3B3', display: 'block', mt: 0.5 }}>
                      {msg.product_msg}
                    </Typography>
                  </Paper>
                )}

                {hasCategoryLimit && (
                  <Paper
                    sx={{
                      p: 1.5,
                      borderRadius: '12px',
                      background: 'rgba(255, 215, 0, 0.08)',
                      border: '1px solid rgba(255, 215, 0, 0.15)'
                    }}
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <LocalOfferIcon sx={{ color: '#FFD700', fontSize: 20 }} />
                        <Typography variant="body2" sx={{ color: '#FFFFFF' }}>
                          {msg.category_limit}
                        </Typography>
                      </Box>
                      <Chip
                        label={`${product.category.card_limit} ${language === 'ar' ? 'منتج' : 'items'}`}
                        size="small"
                        sx={{
                          backgroundColor: '#FFD700',
                          color: '#000000',
                          fontWeight: 700,
                          fontSize: '0.75rem'
                        }}
                      />
                    </Box>
                    <Typography variant="caption" sx={{ color: '#B3B3B3', display: 'block', mt: 0.5 }}>
                      {msg.category_msg}
                    </Typography>
                  </Paper>
                )}
              </Box>
            </Box>

            <Divider sx={{ my: 2, borderColor: 'rgba(255, 215, 0, 0.1)' }} />

            {/* Reason Section */}
            <Box>
              <Typography variant="subtitle2" sx={{ color: '#FFD700', mb: 1.5, fontWeight: 600 }}>
                {msg.reason}
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {msg.reason_text.split('\n').map((line, index) => (
                  <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box
                      sx={{
                        width: 6,
                        height: 6,
                        borderRadius: '50%',
                        backgroundColor: '#FFD700'
                      }}
                    />
                    <Typography variant="body2" sx={{ color: '#B3B3B3' }}>
                      {line}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>

            {/* Thank You Message */}
            <Box
              sx={{
                mt: 3,
                p: 2,
                borderRadius: '12px',
                background: 'linear-gradient(135deg, rgba(255, 215, 0, 0.1) 0%, rgba(255, 215, 0, 0.05) 100%)',
                textAlign: 'center'
              }}
            >
              <Typography variant="body2" sx={{ color: '#FFD700', fontWeight: 500 }}>
                {msg.thank_you}
              </Typography>
            </Box>
          </Box>
        </Fade>
      </DialogContent>

      <DialogActions sx={{ p: 2.5, pt: 0 }}>
        <Button
          fullWidth
          onClick={onClose}
          variant="contained"
          sx={{
            backgroundColor: '#FFD700',
            color: '#000000',
            fontWeight: 800,
            py: 1.5,
            borderRadius: '12px',
            '&:hover': {
              backgroundColor: '#FFE55C',
              transform: 'translateY(-2px)'
            },
            transition: 'all 0.2s ease'
          }}
        >
          {msg.got_it}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default LimitInfoModal;